import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import { trackGoogleAdsEvent } from "./lib/googleAds";
import { trackEvent } from "./lib/analytics";
import { fetchConfig } from "./lib/api";
import { LanguageProvider } from "./context/LanguageContext";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import Home from "./pages/Home";
import { CookieBanner } from "./components/CookieBanner";
import { Toaster } from "./components/ui/sonner";

/* Route-level code splitting: only the landing page ships in the main bundle */
const CartPage = lazy(() => import("./pages/CartPage"));
const Shop = lazy(() => import("./pages/Shop"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const OrdersPage = lazy(() => import("./pages/OrdersPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const InvoicePage = lazy(() => import("./pages/InvoicePage"));
const TrackPage = lazy(() => import("./pages/TrackPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const DropPage = lazy(() => import("./pages/DropPage"));
const LegalPage = lazy(() => import("./pages/Legal").then((m) => ({ default: m.LegalPage })));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("./pages/PaymentCancel"));
const NotFound = lazy(() => import("./pages/NotFound"));

function AdsRouteTracking() {
  const location = useLocation();
  useEffect(() => {
    trackGoogleAdsEvent("page_view", {
      page_location: window.location.href,
      page_path: `${location.pathname}${location.search}`,
      page_title: document.title,
    });
    trackEvent("page_view");
  }, [location.pathname, location.search]);
  return null;
}

/* Page change: instant scroll-to-top + near-instant fade (heavy ridge wipe removed for speed) */
function PageTransition({ children }) {
  const location = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  return (
    <motion.div
      key={location.pathname}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

export default function App() {
  useEffect(() => {
    fetchConfig();
  }, []);
  return (
    <HelmetProvider>
    <LanguageProvider>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <AdsRouteTracking />
            <div className="min-h-screen flex flex-col">
              <Header />
              <main className="flex-1">
                {/* one Suspense around routes AND footer: while a lazy chunk loads, the
                    100vh fallback holds the space and the footer never visibly jumps (CLS) */}
                <Suspense fallback={<div style={{ minHeight: "100vh" }} data-testid="route-loading" />}>
                <PageTransition>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/drops/:key" element={<DropPage />} />
                  <Route path="/shop" element={<Shop />} />
                  <Route path="/magaza" element={<Shop />} />
                  <Route path="/cart" element={<CartPage />} />
                  <Route path="/sepet" element={<CartPage />} />
                  <Route path="/item/:id" element={<ProductDetail />} />
                  <Route path="/urun/:id" element={<ProductDetail />} />
                  <Route path="/login" element={<AuthPage />} />
                  <Route path="/login" element={<AuthPage />} />
                  <Route path="/giris" element={<Navigate to="/login" replace />} />
                  <Route path="/account" element={<OrdersPage />} />
                  <Route path="/hesabim" element={<OrdersPage />} />
                  <Route path="/track" element={<TrackPage />} />
                  <Route path="/takip" element={<TrackPage />} />
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="/payment/success" element={<PaymentSuccess />} />
                  <Route path="/payment/cancel" element={<PaymentCancel />} />
                  <Route path="/invoice/:token" element={<InvoicePage />} />
                  <Route path="/impressum" element={<LegalPage slug="impressum" />} />
                  <Route path="/datenschutz" element={<LegalPage slug="datenschutz" />} />
                  <Route path="/widerruf" element={<LegalPage slug="widerruf" />} />
                  <Route path="/versand" element={<LegalPage slug="versand" />} />
                  <Route path="/agb" element={<LegalPage slug="agb" />} />
                  <Route path="/zahlung" element={<LegalPage slug="zahlung" />} />
                  <Route path="/kontakt" element={<ContactPage />} />
                  <Route path="/iletisim" element={<Navigate to="/kontakt" replace />} />
                  <Route path="/contact" element={<Navigate to="/kontakt" replace />} />
                  <Route path="/odeme/basari" element={<PaymentSuccess />} />
                  <Route path="/odeme/iptal" element={<PaymentCancel />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
                </PageTransition>
                <Footer />
                </Suspense>
              </main>
            <CookieBanner />
              <Toaster
                position="bottom-right"
                toastOptions={{
                  style: {
                    borderRadius: 0,
                    border: "1px solid #09090B",
                    background: "#FFFFFF",
                    color: "#09090B",
                    fontFamily: "'JetBrains Mono', monospace",
                    textTransform: "uppercase",
                    fontSize: "12px",
                  },
                }}
              />
            </div>
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </LanguageProvider>
    </HelmetProvider>
  );
}
